import { createFileRoute } from '@tanstack/react-router';
import { useGetLeaguesQuery } from '@/api/leagues/getLeagues';
import { useGetPickemsQuery } from '@/api/pickems/getPickems';
import { SettingsPageTemplate } from '@/components/templates/SettingsPageTemplate';
import { useSettingsPage } from '@/hooks/useSettingsPage';
import { List, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from '@tanstack/react-router';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';

export const Route = createFileRoute('/dashboard/leagues/')({
  component: LeaguesPage,
});

function LeaguesPage() {
  const { data: leagues = [], isLoading, refetch } = useGetLeaguesQuery();
  const { data: pickems = [] } = useGetPickemsQuery();

  // Count pickems per league
  const getPickemCount = (leagueId: string) => {
    return pickems.filter((pickem: any) => pickem.league_id === leagueId).length;
  };

  const formattedLeagues = leagues.map(league => ({
    ...league,
    id: league.league_id,
    label: league.title || league.name_en || league.name_fr || '',
  }));

  const {
    searchQuery,
    setSearchQuery,
    filteredData: filteredLeagues,
  } = useSettingsPage({
    data: formattedLeagues,
    isLoading,
    refetch,
    settingsKey: 'leagues',
    editRoutePath: '/dashboard/leagues/$id',
    getSearchFields: (league: any) => [
      league.name_en || '',
      league.name_fr || '',
      league.description_en || '',
      league.description_fr || '',
    ],
  });

  const columns = [
    {
      id: 'league_id',
      name: 'ID',
      className: 'w-16',
    },
    {
      id: 'name_en',
      name: 'Name (EN)',
      className: 'font-medium',
    },
    {
      id: 'name_fr',
      name: 'Name (FR)',
      className: 'font-medium',
    },
    {
      id: 'actions',
      name: 'Quick Actions',
      render: (league: any) => {
        const count = getPickemCount(league.league_id);
        return (
          <div className="flex gap-2">
            <Link to="/dashboard/pickems" search={{ league_id: league.league_id }}>
              <Button size="sm" variant="outline" title={`View ${count} Pickems`}>
                <List className="h-4 w-4 mr-1" />
                View {count} Pickems
              </Button>
            </Link>
          </div>
        );
      },
    },
  ];

  // Leagues are read-only, no edit actions
  const actions = undefined;

  // Leagues are read-only, no add action
  const headerActions = null;

  return (
    <SettingsPageTemplate
      title="Leagues"
      subtitle="View leagues for pickem competitions (read-only)"
      data={filteredLeagues}
      isLoading={isLoading}
      searchValue={searchQuery}
      onSearchChange={setSearchQuery}
      searchPlaceholder="Search leagues by name or description..."
      columns={columns}
      actions={actions}
      headerActions={headerActions}
      emptyMessage="No leagues found"
      emptyState={{
        icon: Trophy,
        title: 'No leagues configured',
        description: 'Leagues are managed at the system level.',
        actionLabel: undefined,
        onAction: undefined,
      }}
      breadcrumbs={<Breadcrumbs />}
    />
  );
}
